import { CommonModule } from '@angular/common';
import { ChangeDetectorRef, Component, DestroyRef, EventEmitter, inject, Input, Output } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { catchError, finalize, from, mergeMap, noop, Observable, of, tap } from 'rxjs';

import { Field, IHierarchy, Photo, TableCellKinds } from '@site-mate/dashpivot-shared-library';

import { FormPhotoModalComponent } from 'app/form/form-components/form-photo-modal.component';
import { ErrorHandler } from 'app/shared/service/error-handler.service';
import { ToastrService } from 'app/shared/service/toastr.service';

import { ITaskExecution } from '../model/task-execution.model';
import { SamService } from '../sam.service';

@Component({
  selector: 'cc-ask-sam-to-import',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './ask-sam-to-import.component.html',
  styleUrls: ['./ask-sam-to-import.component.scss'],
})
export class AskSamToImportComponent {
  protected readonly destroyRef = inject(DestroyRef);
  @Input() hierarchy: IHierarchy;
  @Input() formItem: Field;
  @Input() taskId: string;
  @Output() onSamResponseReceived = new EventEmitter<ITaskExecution>();

  isProcessing = false;
  files = {
    processing: 0,
    total: 0,
  };

  constructor(
    private readonly samService: SamService,
    protected readonly modal: NgbModal,
    private changeDetection: ChangeDetectorRef,
    private readonly errorHandler: ErrorHandler,
    private readonly toastrService: ToastrService,
  ) {}

  openPhotoModal() {
    const modalRef = this.modal.open(FormPhotoModalComponent, {
      windowClass: 'form-photo-modal',
      keyboard: false,
      container: '.form-modal-container',
    });

    const componentInstance = modalRef.componentInstance as FormPhotoModalComponent;
    componentInstance.hierarchy = this.hierarchy;
    componentInstance.formRef = true;
    componentInstance.uploaderLabels = {
      title: 'Import',
      subtitle: 'Sam will automatically convert your records into rows in the table.',
      subtitleImage: 'assets/icons/multi-colored-icons/sam-square.svg',
      finishButton: 'Start processing',
    };
    componentInstance.acceptedFiles = 'image/jpg, image/bmp, image/jpeg, image/png, image/tiff';

    modalRef.result.then(this.onUploaderComplete.bind(this)).catch(noop);
  }

  onUploaderComplete(files: Photo[]): void {
    if (files.length === 0) {
      return;
    }

    this.isProcessing = true;
    this.files.processing = 1;
    this.files.total = files.length;
    this.changeDetection.markForCheck();

    from(files)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        mergeMap((file) => this.executeTask(file)),
        tap((response) => {
          this.handleSamResponse(response);
        }),
        finalize(() => {
          this.isProcessing = false;
          this.changeDetection.markForCheck();
          this.toastrService.successByKey('samHasFinishedProcessing');
        }),
      )
      .subscribe();
  }

  private handleSamResponse(response: ITaskExecution) {
    if (response) {
      this.onSamResponseReceived.emit(response);
    }

    if (this.files.processing === this.files.total) {
      return;
    }

    this.files.processing += 1;
    this.changeDetection.markForCheck();
  }

  private addPhotoToRow(response: ITaskExecution, photo: Photo): ITaskExecution {
    const row = response.result?.rows[0];
    const photoColumn = row?.columns.find((column) => column.kind === TableCellKinds.Photo);
    if (photoColumn) {
      photoColumn.photos = [photo];
    }

    return response;
  }

  private executeTask(photo: Photo): Observable<ITaskExecution> {
    const { originalFile } = photo.url;

    return this.samService.executeTask(this.taskId, this.formItem, [originalFile]).pipe(
      takeUntilDestroyed(this.destroyRef),
      tap((response) => this.addPhotoToRow(response, photo)),
      catchError((error) => {
        this.errorHandler.handle(error);
        return of(null);
      }),
    );
  }
}
